import { addDays, format, isToday, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { EVENT_TYPE_CLASSES, UnifiedEvent } from '@/hooks/useUnifiedCalendar';
import { CalendarEventTooltip } from '@/components/calendar/CalendarEventTooltip';

interface Props {
  referenceDate: Date;
  events: UnifiedEvent[];
  onSelect: (event: UnifiedEvent) => void;
  nameById?: Map<string, string>;
}

export function UnifiedWeekGrid({ referenceDate, events, onSelect, nameById = new Map() }: Props) {
  const start = startOfWeek(referenceDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

  const byDate = new Map<string, UnifiedEvent[]>();
  events.forEach((e) => {
    const list = byDate.get(e.date) ?? [];
    list.push(e);
    byDate.set(e.date, list);
  });

  return (
    <div className="grid gap-3 md:grid-cols-7">
      {days.map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const dayEvents = byDate.get(key) ?? [];
        return (
          <div key={key} className="rounded-lg border border-border p-2 min-h-[240px]">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs uppercase text-muted-foreground">
                {format(day, 'EEE', { locale: es })}
              </span>
              <span
                className={cn(
                  'text-sm font-semibold',
                  isToday(day) &&
                    'inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground',
                )}
              >
                {format(day, 'd', { locale: es })}
              </span>
            </div>
            <div className="space-y-1.5">
              {dayEvents.length === 0 && (
                <p className="text-[11px] text-muted-foreground">Sin eventos</p>
              )}
              {dayEvents.map((e) => {
                const mentors = e.mentorIds
                  .map((id) => nameById.get(id))
                  .filter(Boolean) as string[];
                const expert = e.expertId ? nameById.get(e.expertId) : undefined;
                return (
                  <CalendarEventTooltip key={e.id} event={e} nameById={nameById}>
                    <button
                      onClick={() => onSelect(e)}
                      className={cn(
                        'w-full rounded px-2 py-2 text-left text-[11px] leading-snug',
                        e.kind === 'milestone' ? 'border-l-4 font-medium' : 'border',
                        EVENT_TYPE_CLASSES[e.type],
                      )}
                    >
                      <div className="font-medium">{e.title}</div>
                      <div className="opacity-80">{e.contextLabel}</div>
                      {(mentors.length > 0 || expert) && (
                        <div className="opacity-80">
                          {mentors.length > 0 && <>Mentor: {mentors.join(', ')}</>}
                          {mentors.length > 0 && expert && ' · '}
                          {expert && <>Experto: {expert}</>}
                        </div>
                      )}
                      <div className="opacity-80">
                        {e.startTime
                          ? `${e.startTime}${e.endTime ? ` – ${e.endTime}` : ''}`
                          : 'Todo el día'}
                      </div>
                    </button>
                  </CalendarEventTooltip>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
